import { MapsWidget } from '@/components/MapsWidget';
export const frontmatter = {
  category: 'atracao',
  title: 'Phare de Conchas : Comment y accéder et vue à 360° sur l’Ilha do Mel',
  excerpt: 'Guide complet pour l\'ascension du phare de Farol das Conchas sur l\'Ilha do Mel. Sentier, horaires, tarif d\'entrée et meilleur moment pour admirer le coucher du soleil depuis le sommet de l\'île.',
  publishedAt: '2026-04-24',
  author: 'Guia Ilha do Mel',
  tags: ['farol', 'trilha', 'passeio', 'vista-panoramica'],
  coverImage: '/images/praia-do-farol-hero.jpg',
  readingTime: '6 min',
};

Phare de Conchas : Comment y monter et vue à 360°

Le **phare de Conchas** est l'image de carte postale la plus emblématique d'Ilha do Mel (l'Île au Miel). Situé au sommet de Morro do Farol (la colline du Phare), il offre l'une des vues les plus spectaculaires de la côte du Paraná : mer, forêt atlantique et villages, le tout depuis un seul et même point de vue.

Dans ce guide, nous expliquons **comment s'y rendre**, **combien cela coûte**, **à quoi s'attendre** lors de l'ascension et **le meilleur moment** pour y être.

---

Où se situe le phare de Conchas ?

Le phare se trouve sur le **Morro do Farol** (la colline du phare), au centre de l'île, entre la plage de Farol et la plage de Praia Grande. On y accède par un sentier balisé qui part du pied de la colline.

| Informations | Détails |

| ------------------- | -------------------------------- |

| **Localisation** | Morro do Farol, Ilha do Mel — PR |

| **Altitude** | 150 mètres au-dessus du niveau de la mer |

| **Accès** | Sentier de terre et de pierres |

| **Durée de la montée** | 15-20 minutes (allure tranquille) |

| **Difficulté** | Facile à modérée |

---

Comment se rendre au point de départ du sentier

Départ de Nova Brasília

1. Suivez le sentier principal vers le sud (en direction d'Encantadas).
2. Après environ 10 minutes de marche, vous verrez un panneau indiquant « Phare » sur votre gauche.
3. Le sentier du phare monte à flanc de colline.

Départ d'Encantadas

1. Suivez le sentier principal vers le nord (en direction de Nova Brasília).
2. Le panneau indiquant le phare se trouvera sur votre droite après avoir passé la Praia do Farol (Plage du Phare).
3. Distance totale : environ 25 à 30 minutes de marche depuis l’embarcadère d’Encantadas.

---

Le sentier : à quoi s'attendre

L'ascension est **courte mais raide par endroits**. Elle ne nécessite pas d'équipement spécial, mais certaines précautions sont importantes :

- **Chaussures :** Portez des baskets avec des semelles antidérapantes. Les rochers peuvent être glissants, surtout après la pluie.

- **Eau :** Emportez une petite bouteille. Il n’y a pas de vendeurs au sommet.

- **Moustiques :** Un répulsif anti-moustiques est utile dans les zones de forêt dense.

- **Parapluie ou imperméable :** Le temps change rapidement sur l’île.

**Conseil local :** Montez en fin d’après-midi (une heure avant le coucher du soleil). La lumière dorée sur la mer est digne d’un film, et la température plus fraîche rend l’ascension plus agréable.

---

En haut : la vue à 360°

Une fois arrivé au sommet, vous trouverez :

- Le **phare historique** (construit en 1872, toujours en activité)

- Une **aire d'observation** entourée de barrières de sécurité

- Des panneaux informatifs retracent l'histoire du phare et de l'île

Ce que l'on peut voir de chaque côté :

| Direction | Vue |

| --------- | -------------------------------------------------- |

| **Est** | Océan Atlantique et horizon infini |

| **Sud** | Plage d'Encantadas et continent en arrière-plan |

| **Ouest** | Baie de Paranaguá, mangroves et Serra do Mar |

| **Nord** | Praia Grande, Nova Brasília et plages côtières |

Par temps clair, il est possible d'apercevoir le **port de Paranaguá** et même la ville de Paranaguá au loin.

---

Horaires et tarifs

| Article | Détails |

| ---------------------------- | -------------------------------------- |

| **Horaires d'ouverture** | 9h à 17h (tous les jours) |

| **Entrée** | Gratuite |

| **Meilleur moment** | 16h30-17h (coucher du soleil) |

| **Restrictions** | Il est interdit de grimper sur la structure du phare |

**Attention :** Le phare ferme au crépuscule pour des raisons de sécurité. Ne restez pas au sommet après la fermeture ; descendre dans l’obscurité est dangereux.

---

## Photos : comment immortaliser la vue

- **Objectif grand angle** (16-24 mm) : capture toute l'étendue du paysage.

- **L'heure dorée** (une heure avant le coucher du soleil) offre une lumière chaude et des ombres allongées.

- **Trépied léger** : utile pour les poses longues de la mer au crépuscule.

- **Drone** : le survol est autorisé, mais attention aux vents forts.

---

Règles de visite

- Ne laissez pas de déchets sur le sentier ni au sommet.

- Ne grattez pas et n'endommagez pas les structures du phare.

- Respectez le silence : c'est un lieu de recueillement.

- Les animaux domestiques sont admis sur le sentier, mais doivent être tenus en laisse.

---

Combinez avec d'autres circuits

Le phare de Farol das Conchas se situe à mi-chemin entre les deux villages. Combinez votre visite avec :

- **Plage de Farol** : plage située juste en contrebas de la colline, idéale pour se détendre après l’ascension.

- **Sentier Enchanté** : prolongement du sentier principal menant au village sud.

- **Grande Plage** : la plus grande étendue de sable de l’île, à 10 minutes à pied.

<MapsWidget query="Farol das Conchas Ilha do Mel" />

---

Vous souhaitez organiser un itinéraire complet pour l'Ilha do Mel ? Notre [guide complet de l'Ilha do Mel](/guide-complete) vous propose des sentiers de randonnée, des plages, des restaurants et des conseils d'habitants pour profiter pleinement de l'île.